import { useRef, useState } from 'react';
import { useAddEventPhoto, useDeleteEventPhoto } from '../../events/mutations';
import type { EventPhoto } from '../../events/types';
import { Button, ConfirmDialog, EmptyState, Heading, Stack } from '../../ui';

/** Props for {@link EventPhotos}. */
export interface EventPhotosProps {
  readonly eventId: string;
  readonly photos: readonly EventPhoto[];
  readonly canEdit: boolean;
}

/**
 * An event's photo gallery, with upload and delete for the owning organiser or an administrator.
 *
 * Uploads go through the shared image pipeline, so EXIF — including the GPS of a public venue — is
 * stripped server-side. Everyone else sees the gallery read-only.
 *
 * @param props - Component props.
 * @returns The photos section.
 */
export function EventPhotos({ eventId, photos, canEdit }: EventPhotosProps): React.JSX.Element {
  const add = useAddEventPhoto(eventId);
  const remove = useDeleteEventPhoto(eventId);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File): Promise<void> {
    setError(null);
    try {
      await add.mutateAsync(file);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not upload that photo.');
    }
  }

  return (
    <Stack gap="md">
      <Stack direction="row" gap="md" align="center" justify="space-between" wrap>
        <Heading level={2} size="lg">
          Photos
        </Heading>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) {
                  void upload(file);
                }
                event.target.value = '';
              }}
            />
            <Button variant="primary" disabled={add.isPending} onClick={() => inputRef.current?.click()}>
              {add.isPending ? 'Uploading…' : 'Add a photo'}
            </Button>
          </>
        )}
      </Stack>

      {error !== null && (
        <p className="gm-field-error" role="alert">
          {error}
        </p>
      )}

      {photos.length === 0 ? (
        <EmptyState title="No photos yet" description="Photos from the venue appear here." />
      ) : (
        <div className="gm-grid">
          {photos.map((photo) => (
            <Stack key={photo.id} gap="xs">
              <img
                src={photo.url}
                alt="Event"
                style={{ width: '100%', borderRadius: 12, aspectRatio: '3 / 2', objectFit: 'cover' }}
              />
              {canEdit && (
                <Button variant="danger" onClick={() => setPendingDelete(photo.id)}>
                  Remove
                </Button>
              )}
            </Stack>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove this photo?"
        message="It is permanently removed from the listing."
        confirmLabel="Remove"
        destructive
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete !== null) {
            remove.mutate(pendingDelete);
          }
          setPendingDelete(null);
        }}
      />
    </Stack>
  );
}
